import React, { useEffect, useMemo, useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { useUser } from "@clerk/react";
import { Layout } from "@/components/layout";
import {
  useGetCourse,
  useListMyEnrollments,
  useListMySnippets,
  useListMyMessages,
  getGetCourseQueryKey,
  getListMyEnrollmentsQueryKey,
  getListMySnippetsQueryKey,
  getListMyMessagesQueryKey,
} from "@workspace/api-client-react";
import type {
  CourseDetail,
  CodeSnippetWithLesson,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  ExternalLink,
  FileText,
  Code,
  Terminal,
  CheckCircle2,
  Megaphone,
} from "lucide-react";
import { dateLongFmt } from "@/lib/format";
import { CodeSandbox } from "@/components/sandbox/code-sandbox";
import { LessonVideoPlayer } from "@/components/lesson-video-player";
import {
  isJoinable,
  googleCalendarUrl,
  formatLocalSessionTime,
} from "@/lib/calendar";

type Lesson = CourseDetail["lessons"][number];

export default function Classroom() {
  const [, params] = useRoute("/learn/:slug");
  const slug = params?.slug || "";
  const [, navigate] = useLocation();

  const { data: course, isLoading, isError } = useGetCourse(slug, {
    query: {
      enabled: !!slug,
      queryKey: getGetCourseQueryKey(slug),
      // Poll every 20s on live cohorts so the "Join live now" CTA appears as
      // soon as the teacher starts a Zoom session, without a manual refresh.
      refetchInterval: (query) =>
        query.state.data?.format === "live" ? 20_000 : false,
      refetchIntervalInBackground: false,
    },
  });

  const { isSignedIn, isLoaded: userLoaded } = useUser();

  const enrollmentsEnabled = !!isSignedIn;
  const { data: myEnrollments, isLoading: enrollmentsLoading } =
    useListMyEnrollments({
      query: {
        enabled: enrollmentsEnabled,
        queryKey: getListMyEnrollmentsQueryKey(),
      },
    });

  const { data: mySnippets } = useListMySnippets({
    query: {
      enabled: !!isSignedIn,
      queryKey: getListMySnippetsQueryKey(),
    },
  });

  const { data: allMessages } = useListMyMessages({
    query: {
      enabled: !!isSignedIn,
      queryKey: getListMyMessagesQueryKey(),
    },
  });

  const courseMessages = useMemo(
    () => (course ? allMessages?.filter((m) => m.courseId === course.id) ?? [] : []),
    [allMessages, course],
  );

  const isEnrolled = !!(
    course &&
    myEnrollments?.some(
      (e) => e.course.id === course.id && e.status === "active",
    )
  );

  // Track active lesson via URL hash so deep-links + browser nav work.
  // Hash uses lesson orderIndex (e.g. #lesson-3 = the 3rd lesson) so links
  // are stable across DB reseeds.
  const [activeLessonId, setActiveLessonId] = useState<number | null>(null);

  const resolveFromHash = React.useCallback(
    (lessons: Lesson[]) => {
      const order = parseInt(
        window.location.hash.replace(/^#lesson-/, ""),
        10,
      );
      const valid = lessons.find((l) => l.orderIndex === order);
      return valid ? valid.id : lessons[0]!.id;
    },
    [],
  );

  useEffect(() => {
    if (!course || course.lessons.length === 0) return;
    setActiveLessonId(resolveFromHash(course.lessons));
    const onHash = () => setActiveLessonId(resolveFromHash(course.lessons));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [course, resolveFromHash]);

  const activeLesson: Lesson | undefined = useMemo(
    () => course?.lessons.find((l) => l.id === activeLessonId),
    [course, activeLessonId],
  );

  const handleSelect = (lesson: Lesson) => {
    setActiveLessonId(lesson.id);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", `#lesson-${lesson.orderIndex}`);
    }
  };

  // Loading + access guards
  if (isLoading || !userLoaded || (enrollmentsEnabled && enrollmentsLoading)) {
    return (
      <Layout>
        <div className="p-24 text-center" role="status" aria-live="polite">
          Loading classroom&hellip;
        </div>
      </Layout>
    );
  }

  if (isError || !course) {
    return (
      <Layout>
        <div className="p-24 text-center space-y-4">
          <h1 className="text-2xl font-bold">Course not found</h1>
          <Button asChild variant="outline">
            <Link href="/my-classes">Back to my classes</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  if (!isSignedIn) {
    const redirect = encodeURIComponent(`/learn/${slug}`);
    navigate(`/sign-in?redirect_url=${redirect}`);
    return null;
  }

  if (!isEnrolled) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-24 max-w-xl text-center space-y-6">
          <h1 className="font-display text-3xl font-bold">
            You&rsquo;re not enrolled in this course
          </h1>
          <p className="text-muted-foreground">
            Enroll first to access the classroom and lesson materials.
          </p>
          <div className="flex gap-3 justify-center">
            <Button asChild>
              <Link href={`/courses/${course.slug}`}>View course</Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/my-classes">My classes</Link>
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const isLive = course.format === "live";

  return (
    <Layout>
      {/* Classroom header */}
      <div className="border-b border-border bg-foreground text-background relative overflow-hidden">
        <div
          className="absolute inset-0 bg-grid-sm opacity-[0.06] pointer-events-none"
          aria-hidden="true"
        />
        <div className="container mx-auto px-4 py-10 md:py-12 relative z-10">
          <Link
            href="/my-classes"
            className="inline-flex items-center font-mono text-[11px] uppercase tracking-[0.25em] text-background/70 hover:text-secondary mb-6 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-2" /> My classes
          </Link>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-secondary mb-3 flex flex-wrap gap-x-3 gap-y-1">
            <span>[ classroom ]</span>
            <span className="text-background/30">/</span>
            <span>{isLive ? "live cohort" : "self-paced"}</span>
            <span className="text-background/30">/</span>
            <span>{course.lessons.length} lessons</span>
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-balance">
            {course.title}
          </h1>
        </div>
      </div>

      {courseMessages.length > 0 && (
        <div className="border-b border-border bg-secondary/10">
          <div className="container mx-auto px-4 py-6">
            <div className="flex items-center gap-2 mb-3">
              <Megaphone className="w-4 h-4 text-primary" aria-hidden="true" />
              <h2 className="font-display text-lg font-bold">Classroom updates</h2>
              <Badge variant="outline" className="rounded-full ml-auto">
                {courseMessages.length}
              </Badge>
            </div>
            <ul className="space-y-2">
              {courseMessages.slice(0, 3).map((m) => (
                <li
                  key={m.id}
                  className="border border-border rounded-xl p-4 bg-background"
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <div className="min-w-0">
                      <div className="font-bold leading-tight">{m.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Posted by {m.teacherName}
                      </div>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
                      {dateLongFmt.format(new Date(m.createdAt))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground/90 whitespace-pre-wrap mt-1.5">
                    {m.body}
                  </p>
                </li>
              ))}
            </ul>
            {courseMessages.length > 3 && (
              <div className="mt-3 text-right">
                <Button asChild variant="ghost" size="sm" className="rounded-full">
                  <Link href="/my-classes">View all in My classes</Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="container mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-10">
        {/* Lesson rail (vertical tab list) */}
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div
            role="tablist"
            aria-orientation="vertical"
            aria-label="Lessons"
            className="flex flex-col gap-1 bg-card border rounded-2xl p-2 shadow-sm"
          >
            {course.lessons.map((lesson) => {
              const active = lesson.id === activeLesson?.id;
              return (
                <button
                  key={lesson.id}
                  role="tab"
                  aria-selected={active}
                  aria-controls={`lesson-panel-${lesson.id}`}
                  id={`lesson-tab-${lesson.id}`}
                  onClick={() => handleSelect(lesson)}
                  className={
                    "text-left rounded-xl px-3 py-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
                    (active
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-muted text-foreground")
                  }
                >
                  <div
                    className={
                      "font-mono text-[10px] uppercase tracking-widest mb-1 " +
                      (active ? "text-primary-foreground/80" : "text-muted-foreground")
                    }
                  >
                    Lesson {String(lesson.orderIndex).padStart(2, "0")}
                  </div>
                  <div className="font-medium leading-snug">{lesson.title}</div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Active lesson panel */}
        {activeLesson && (
          <LessonPanel
            key={activeLesson.id}
            lesson={activeLesson}
            isLive={isLive}
            courseSlug={course.slug}
            savedSnippet={
              mySnippets?.find((s) => s.lessonId === activeLesson.id) ?? null
            }
          />
        )}
      </div>
    </Layout>
  );
}

function LiveSessionBlock({ lesson }: { lesson: Lesson }) {
  // Re-render every 30s so the Join button flips on at the start of the
  // window without the student having to manually refresh. Cheap because the
  // page is small and only mounted while the student is actively viewing.
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const joinable = isJoinable(lesson);
  const teacherStarted = !!lesson.zoomLiveStartedAt;

  if (joinable && lesson.zoomUrl) {
    return (
      <div className="bg-secondary/15 border-2 border-secondary rounded-2xl p-8 text-center shadow-sm flex flex-col items-center justify-center">
        <div className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-red-600 mb-3">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" aria-hidden="true" />
          {teacherStarted ? "Live now" : "Session window open"}
        </div>
        <h3 className="text-xl font-bold mb-1">
          {teacherStarted ? "Class is in session" : "Time to join"}
        </h3>
        <p className="text-muted-foreground mb-6">
          {teacherStarted && lesson.zoomLiveStartedAt
            ? `Started ${formatLocalSessionTime(lesson.zoomLiveStartedAt)}`
            : lesson.zoomScheduledAt
              ? `Scheduled ${formatLocalSessionTime(lesson.zoomScheduledAt)}`
              : null}
        </p>
        <Button asChild size="lg">
          <a href={lesson.zoomUrl} target="_blank" rel="noopener noreferrer">
            Join live now <ExternalLink className="ml-2 w-4 h-4" />
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-2xl p-8 text-center shadow-sm flex flex-col items-center justify-center">
      <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
        <CalendarIcon className="w-8 h-8" />
      </div>
      <h3 className="text-xl font-bold mb-1">Live Zoom Session</h3>
      {lesson.zoomScheduledAt ? (
        <p className="text-muted-foreground mb-6">
          {formatLocalSessionTime(lesson.zoomScheduledAt)}
        </p>
      ) : (
        <p className="text-muted-foreground mb-6">Schedule pending</p>
      )}
      {lesson.zoomScheduledAt && (
        <Button asChild variant="outline" size="sm" className="mb-3">
          <a
            href={googleCalendarUrl({
              title: `Live · Lesson ${String(lesson.orderIndex).padStart(2, "0")}: ${lesson.title}`,
              start: new Date(lesson.zoomScheduledAt),
              description: lesson.summary,
              location: lesson.zoomUrl ?? undefined,
            })}
            target="_blank"
            rel="noopener noreferrer"
          >
            <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
            Add to Google Calendar
          </a>
        </Button>
      )}
      <Button size="lg" disabled>
        {lesson.zoomUrl ? "Join opens 15 min before start" : "Link not available yet"}
      </Button>
    </div>
  );
}

function LessonPanel({
  lesson,
  isLive,
  courseSlug,
  savedSnippet,
}: {
  lesson: Lesson;
  isLive: boolean;
  courseSlug: string;
  savedSnippet: CodeSnippetWithLesson | null;
}) {
  return (
    <section
      role="tabpanel"
      id={`lesson-panel-${lesson.id}`}
      aria-labelledby={`lesson-tab-${lesson.id}`}
      className="space-y-10"
    >
      <header className="space-y-3">
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
          Lesson {String(lesson.orderIndex).padStart(2, "0")}
          <span className="text-muted-foreground"> · </span>
          <span className="text-muted-foreground">
            {isLive ? "live · zoom session" : "video lecture"}
          </span>
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-balance">
          {lesson.title}
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl">
          {lesson.summary}
        </p>
      </header>

      {/* Media block */}
      {isLive ? (
        <LiveSessionBlock lesson={lesson} />
      ) : (
        <LessonVideoPlayer
          lessonId={lesson.id}
          hasVideo={!!lesson.hasVideo}
          durationMinutes={lesson.videoDurationMinutes}
          courseSlug={courseSlug}
        />
      )}

      {/* Sandbox */}
      {(lesson.sandboxLanguage === "python" ||
        lesson.sandboxLanguage === "web") &&
        lesson.sandboxStarter && (
          <div>
            <h3 className="text-xl font-bold border-b pb-3 mb-4 flex items-center gap-2">
              <Terminal className="w-5 h-5 text-primary" /> Code Sandbox
            </h3>
            <CodeSandbox
              language={lesson.sandboxLanguage}
              starter={lesson.sandboxStarter}
              lessonId={lesson.id}
              lessonTitle={lesson.title}
              initialSnippet={savedSnippet}
            />
          </div>
        )}

      {/* Readings */}
      <div>
        <h3 className="text-xl font-bold border-b pb-3 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> Required Reading
        </h3>
        {lesson.readings.length > 0 ? (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {lesson.readings.map((reading) => {
              const inner = (
                <>
                  <div className="font-medium mb-1 group-hover:text-primary transition-colors">
                    {reading.title}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center justify-between">
                    <span>{reading.estimatedMinutes} min read</span>
                    {reading.url && (
                      <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    )}
                  </div>
                </>
              );
              return (
                <li key={reading.id} className="group">
                  {reading.url ? (
                    <a
                      href={reading.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 rounded-xl border bg-muted/30 hover:bg-muted transition-colors"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div className="block p-4 rounded-xl border bg-muted/30">
                      {inner}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No readings for this lesson.
          </p>
        )}
      </div>

      {/* Homework */}
      <div>
        <h3 className="text-xl font-bold border-b pb-3 mb-4 flex items-center gap-2">
          <Code className="w-5 h-5 text-secondary" /> Homework
        </h3>
        {lesson.homework.length > 0 ? (
          <ul className="space-y-5">
            {lesson.homework.map((hw) => (
              <li
                key={hw.id}
                className="border rounded-xl p-5 bg-card shadow-sm"
              >
                <div className="flex justify-between items-start mb-2 gap-3">
                  <div className="font-bold">{hw.title}</div>
                  <Badge variant="outline" className="text-xs shrink-0">
                    {hw.estimatedMinutes} min
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {hw.instructions}
                </p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No homework for this lesson.
          </p>
        )}
      </div>

      <div className="pt-4">
        <Button asChild variant="outline">
          <Link href={`/lessons/${lesson.id}`}>
            Open lesson page <CheckCircle2 className="ml-2 w-4 h-4" />
          </Link>
        </Button>
      </div>
    </section>
  );
}
